<template>
  <div
    ref="rootEl"
    class="guidance-tooltip"
    :class="{
      'guidance-tooltip--has-video': config.videoUrl,
      'guidance-tooltip--dock-bottom': config.tooltipPosition === 'center-bottom' && !isMobile,
    }"
    :style="[positionStyle, config.tooltipMaxWidth ? { maxWidth: `${config.tooltipMaxWidth}px` } : {}]"
    @click.stop
  >
    <GuidanceVideo
      v-if="config.videoUrl"
      :url="config.videoUrl"
      :description="stepText"
    />
    <div class="guidance-tooltip__body">
      <div
        v-if="kind === 'onboarding'"
        class="guidance-tooltip__meta"
      >
        <span class="guidance-tooltip__step-count">
          {{ t("guidance.stepOf", { current: config.step, total: totalSteps }) }}
        </span>
        <button
          type="button"
          class="guidance-tooltip__close"
          :title="t('guidance.closeTourButton')"
          @click="emit('close')"
        >
          <i class="dx-icon-close"></i>
        </button>
      </div>

      <div
        v-if="config.titleKey"
        class="guidance-tooltip__title"
      >
        {{ t(config.titleKey) }}
      </div>
      <div
        class="guidance-tooltip__text"
        v-html="stepText"
      />
      <img
        v-if="config.imageUrl"
        :src="config.imageUrl"
        class="guidance-tooltip__image"
        alt=""
      />
      <div
        v-if="config.hintKey"
        class="guidance-tooltip__hint"
        :class="{ 'guidance-tooltip__hint--callout': kind === 'onboarding' }"
      >
        <CartIcon
          v-if="config.hintIcon === 'cart'"
          class="guidance-tooltip__hint-icon"
        />
        <span v-html="t(config.hintKey)" />
      </div>
      <!-- Зелёная строка «Готово!...» — только тур работы (ТЕСТ ОНБОРДИНГ 2, шаг 2:
           у онбординга это дублирует текст самого шага). -->
      <div
        v-if="kind !== 'onboarding' && config.advanceMode === 'interact' && readyToAdvance"
        class="guidance-tooltip__click-hint guidance-tooltip__click-hint--ready"
      >
        {{ t("guidance.readyToAdvanceHint") }}
      </div>
      <div
        v-else-if="kind !== 'onboarding' && config.advanceMode !== 'button'"
        class="guidance-tooltip__click-hint"
      >
        {{ t("guidance.clickHint") }}
      </div>
      <div class="guidance-tooltip__divider" />
      <div class="guidance-tooltip__footer">
        <DxButton
          v-if="canGoBack"
          :text="t('guidance.backButton')"
          :title="t('guidance.backButtonTitle')"
          type="normal"
          styling-mode="outlined"
          class="guidance-tooltip__back-btn"
          @click="emit('goBack')"
        />

        <template v-if="kind === 'onboarding'">
          <!-- Единый набор кнопок на всех шагах онбординга (ТЕСТ ОНБОРДИНГ §0):
               «Пропустить шаг» и «Далее» доступны всегда, а не только на
               interact-шагах после выполнения условия (ТЕСТ ОНБОРДИНГ 2, п.30) —
               «Стоп» убрана, выход теперь через крестик в шапке тултипа. -->
          <DxButton
            :text="t('guidance.skipStepButton')"
            :title="t('guidance.skipStepButtonTitle')"
            type="normal"
            styling-mode="text"
            class="guidance-tooltip__skip-btn"
            @click="emit('advance')"
          />
          <DxButton
            :text="t('guidance.nextButton')"
            type="default"
            styling-mode="contained"
            class="guidance-tooltip__next-btn"
            @click="emit('advance')"
          />
        </template>
        <template v-else>
          <DxButton
            v-if="config.advanceMode === 'interact' && !readyToAdvance"
            :text="t('guidance.skipStepButton')"
            :title="t('guidance.skipStepButtonTitle')"
            type="normal"
            styling-mode="text"
            class="guidance-tooltip__skip-btn"
            @click="emit('advance')"
          />
          <DxButton
            :text="t('guidance.stopButton')"
            type="normal"
            styling-mode="outlined"
            class="guidance-tooltip__stop-btn"
            @click="emit('stop')"
          />
          <DxButton
            v-if="config.advanceMode === 'button' || readyToAdvance"
            :text="t('guidance.nextButton')"
            type="default"
            styling-mode="contained"
            class="guidance-tooltip__next-btn"
            @click="emit('advance')"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useBreakpoints } from "../common/composables/useBreakpoints";
import DxButton from "devextreme-vue/button";
import GuidanceVideo from "./GuidanceVideo.vue";
import CartIcon from "../assets/icons/CartIcon.vue";
import type { GuidanceKind, GuidanceStepConfig } from "../models/guidance/GuidanceTypes";

interface Props {
  config: GuidanceStepConfig;
  positionStyle: Record<string, string>;
  canGoBack: boolean;
  readyToAdvance?: boolean;
  kind: GuidanceKind;
  totalSteps: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  advance: [];
  stop: [];
  goBack: [];
  close: [];
}>();

const { t } = useI18n();
const { isMobile } = useBreakpoints();

// Шаг может объяснять то же действие другим путём: на телефоне часть
// инструментов живёт в меню «⋮», а не в строке поиска
const stepText = computed(() =>
  isMobile.value && props.config.mobileTextKey ? t(props.config.mobileTextKey) : t(props.config.textKey),
);

const rootEl = ref<HTMLElement | null>(null);

defineExpose({ rootEl });
</script>
